import { motion } from "framer-motion";
import { Camera, Video, Mic, MessageSquare, SquareKanban, TrendingUp, Tag } from "lucide-react";

const TYPE_ICON = { screenshot: Camera, screen_recording: Video, voice: Mic, text: MessageSquare };

const ROWS = [
  { type: "screen_recording", title: "Transfer button spins forever on submit", who: "Jane · acme.com/pay", dot: "bg-danger", tags: ["bug", "vip-client"], jira: "FB-128" },
  { type: "screenshot", title: "Login form misaligned on iPad", who: "Marcus · staging.app", dot: "bg-sunny", tags: ["ux"] },
  { type: "voice", title: "Voice note — onboarding is confusing", who: "Priya · beta.app", dot: "bg-sunny", tags: ["feedback"] },
  { type: "text", title: "Typo in the export modal header", who: "Anonymous", dot: "bg-grass", tags: ["copy"], resolved: true },
];

const TREND = [2, 3, 1, 4, 3, 5, 4, 6, 5, 7, 6, 8];

function Callout({ children, className = "" }) {
  return (
    <span className={`absolute z-20 text-[11px] font-800 px-3 py-1.5 rounded-full border-2 border-ink shadow-nb-sm whitespace-nowrap ${className}`}>
      {children}
    </span>
  );
}

export default function DashboardPreview() {
  return (
    <div className="relative" data-testid="dashboard-preview">
      {/* Floating callouts (desktop only) */}
      <Callout className="hidden lg:block -top-3 left-8 bg-teal">↓ Tag &amp; triage every item</Callout>
      <Callout className="hidden lg:block top-[62%] -right-3 bg-ink text-cream">Auto-resolved from Jira →</Callout>

      {/* Browser chrome */}
      <div className="nb-card p-0 overflow-hidden bg-cream">
        <div className="flex items-center gap-2 px-4 py-3 border-b-2 border-ink bg-white">
          <span className="h-3 w-3 rounded-full border-2 border-ink bg-danger" />
          <span className="h-3 w-3 rounded-full border-2 border-ink bg-sunny" />
          <span className="h-3 w-3 rounded-full border-2 border-ink bg-grass" />
          <span className="ml-3 font-mono text-xs text-ink/50">app.tugboat.dev/dashboard</span>
        </div>

        <div className="p-5 sm:p-6">
          {/* Metric strip */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="nb-card bg-orange text-white p-4">
              <p className="text-[10px] font-800 uppercase tracking-wide text-white/90">Total feedback</p>
              <p className="font-display text-3xl font-700 leading-none mt-1">248</p>
            </div>
            <div className="nb-card bg-white p-4">
              <p className="text-[10px] font-800 uppercase tracking-wide text-ink/60">New</p>
              <p className="font-display text-3xl font-700 leading-none mt-1 text-danger">12</p>
            </div>
            <div className="nb-card bg-white p-4">
              <p className="text-[10px] font-800 uppercase tracking-wide text-ink/60">Resolved</p>
              <p className="font-display text-3xl font-700 leading-none mt-1 text-grass">196</p>
            </div>
          </div>

          {/* Trend mini-chart */}
          <div className="nb-card bg-white p-4 mb-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-800 uppercase tracking-wide text-ink/60 flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5 text-teal" /> Feedback this month</p>
              <span className="font-mono text-xs text-ink/50">+38%</span>
            </div>
            <div className="flex items-end gap-1.5 h-16">
              {TREND.map((v, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  whileInView={{ height: `${(v / 8) * 100}%` }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04, type: "spring", stiffness: 200, damping: 18 }}
                  className="flex-1 bg-teal border-2 border-ink rounded-t-md"
                />
              ))}
            </div>
          </div>

          {/* Inbox */}
          <p className="label-overline mb-2">Feedback inbox</p>
          <div className="nb-card p-0 overflow-hidden divide-y-2 divide-ink/10 bg-white">
            {ROWS.map((r, i) => {
              const Icon = TYPE_ICON[r.type];
              return (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <span className={`h-2.5 w-2.5 rounded-full border-2 border-ink shrink-0 ${r.dot}`} />
                  <span className="h-8 w-8 bg-cream border-2 border-ink rounded-lg flex items-center justify-center shrink-0">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-700 truncate">{r.title}</p>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <span className="text-[10px] text-ink/55 font-600 truncate">{r.who}</span>
                      {r.tags.map((t) => (
                        <span key={t} className="inline-flex items-center gap-0.5 text-[9px] font-800 px-1.5 py-0.5 rounded-full border border-ink bg-teal">
                          <Tag className="h-2 w-2" />{t}
                        </span>
                      ))}
                    </div>
                  </div>
                  {r.jira && (
                    <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-800 px-2 py-1 rounded-full border-2 border-ink bg-white shrink-0">
                      <SquareKanban className="h-3 w-3 text-teal" /> {r.jira}
                    </span>
                  )}
                  {r.resolved && (
                    <span className="text-[9px] font-800 uppercase px-2 py-1 rounded-full border-2 border-grass bg-grass/15 text-grass shrink-0">Resolved</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
